/**
 * This is our global tracking file that integrates with mixpanel
 * By default this module will return a wrapper around the `react-tracking` module, but
 *  all individual tracking events can still be used for any type of project
 *
 * Copyright (c) 2018 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import track from 'react-tracking'
import isFunction from 'lodash/isFunction'

/**
 * Handle any tracking data object as long as it's in the proper format
 * @param {Object} trackingData The meta object that holds the tracking data.
 *
 * Example:
 *
 *  {
 *    event: {
 *      name: 'Anchor Link',
 *      props: {
 *        'Label': 'Take quiz'
 *      }
 *    },
 *    page: {
 *      location: '/quiz'
 *    },
 *    user: {
 *      id: '9sadiDFgiod46askfA',
 *      props: {
 *        $email: 'test@email.com
 *      }
 *    }
 *
 *  }
 *
 */
export const trackUniversalEvent = (trackingData) => {
  if (trackingData.user) {
    const { id, isNewUser, props } = trackingData.user

    if (id) {
      if (isNewUser) {
        setNewUser(id)
      } else {
        setUser(id)
      }
    }

    if (props) {
      setUserProperties(props)
    }
  }

  if (trackingData.page) {
    trackPage(trackingData.page)
  }

  if (trackingData.event) {
    const { name, props } = trackingData.event
    trackEvent(name, props)
  }

  if (trackingData.board) {
    const { name, props, data } = trackingData.board
    trackBoard(name, props, data)
  }
}

export const trackPage = (props = {}) => {
  if (window.mixpanel) {
    const pageViewName = (props['PageName']) ? `Page View - ${props['PageName']}` : 'Page View'
    const uid = _getDistinctId()
    const updatedProps = {
      ...(uid ? { uid } : {}),
      ...props,
      Location: props['Location'] ?? window.location.pathname
    }

    mixpanel.track(pageViewName, updatedProps)
  }
}

export const trackEvent = (eventName, props = {}) => {
  if (window.mixpanel) {
    const uid = _getDistinctId()

    mixpanel.track(eventName, {
      ...(uid ? { uid } : {}),
      ...props
    })
  }
}

// Should only be used on new user sign up
export const setNewUser = (userId) => {
  if (window.mixpanel) {
    mixpanel.alias(userId)
  }
}

export const setUser = (userId) => {
  if (window.mixpanel) {
    mixpanel.identify(userId)
  }
}

export const resetUser = () => {
  if (window.mixpanel) {
    mixpanel.reset()
  }
}

export const deleteUser = () => {
  if (window.mixpanel) {
    mixpanel.people.delete_user()
  }
}

// Special properties: https://mixpanel.com/help/reference/http#people-special-properties
export const setUserProperties = (props) => {
  if (window.mixpanel) {
    mixpanel.people.set(props)
  }
}

// Clears distinct id and all super properties
export const reset = () => {
  if (window.mixpanel) {
    mixpanel.reset()
  }
}

// Custom track event created to track board events
export const trackBoard = (eventName, props = {}, boardData = {}) => {
  if (window.mixpanel) {
    const { bid, location, owner, settings } = boardData
    const trackableBoardData = {
      ...(bid ? { bid } : {}),
      ...(location?.displayText ? { location: location.displayText } : {}),
      ...(owner?.uid ? { owner: owner.uid } : {}),
      ...(settings ? { isPrivate: (settings.isPrivate || false) } : {})
    }

    trackEvent(`Board - ${eventName}`, {
      ...props,
      ...trackableBoardData
    })
  }
}

// Returns the current user id from the mixpanel distinct id
const _getDistinctId = () => {
  if (window.mixpanel && isFunction(mixpanel.get_distinct_id)) {
    return mixpanel.get_distinct_id() || null
  }

  return null
}

export default track
